import { useState, useRef, useEffect } from "react";
import { 
  Bell, 
  Check, 
  Package, 
  User, 
  CheckCircle2, 
  XCircle, 
  Info, 
  AlertTriangle
} from "lucide-react";
import { useNotifications } from "../context/NotificationContext";

export default function NotificationDropdown() {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case "product":
      case "needs_review":
        return <Package size={16} className="text-[#0C6170]" />;
      case "user":
        return <User size={16} className="text-[#37BEB0]" />;
      case "success":
        return <CheckCircle2 size={16} className="text-emerald-500" />;
      case "error":
        return <XCircle size={16} className="text-rose-500" />;
      case "warning":
        return <AlertTriangle size={16} className="text-amber-500" />;
      default:
        return <Info size={16} className="text-[#0C6170]" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case "product":
      case "needs_review":
        return "bg-[#0C6170]/10";
      case "user":
        return "bg-[#37BEB0]/10";
      case "success":
        return "bg-emerald-50";
      case "error":
        return "bg-rose-50";
      case "warning":
        return "bg-amber-50";
      default:
        return "bg-[#0C6170]/5";
    }
  };

  const formatTime = (timeStr) => {
    try {
      const date = new Date(timeStr);
      if (isNaN(date.getTime())) return timeStr;
      const seconds = Math.floor((new Date() - date) / 1000);
      if (seconds < 5) return "Just now";
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days === 1) return "Yesterday";
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    } catch (e) {
      return timeStr;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-2.5 rounded-full text-[#0C6170] hover:text-[#094e5c] hover:bg-[#0C6170]/5 transition-all focus:outline-none flex items-center justify-center border border-[#0000001A] bg-white/70 backdrop-blur-[10px]"
        title="Notifications"
      >
        <Bell size={20} className={unreadCount > 0 ? "bell-wiggle" : ""} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-white animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="fixed right-4 left-auto top-[92px] w-[calc(100vw-32px)] max-w-[360px] md:absolute md:right-0 md:top-[calc(100%+8px)] md:w-[360px] md:left-auto max-h-[500px] bg-white/95 backdrop-blur-[15px] rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#0000001A] flex flex-col z-50 overflow-hidden transition-all duration-200">
          
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-150 flex items-center justify-between bg-gray-50/50">
            <div>
              <h3 className="text-sm font-extrabold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <p className="text-[11px] text-[#0C6170] font-semibold mt-0.5">
                  You have {unreadCount} unread messages
                </p>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-[11px] font-bold text-[#0C6170] hover:text-[#094e5c] flex items-center gap-1 transition-colors"
              >
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto max-h-[300px] divide-y divide-gray-100 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-5 text-center">
                <div className="w-12 h-12 rounded-full bg-[#0C6170]/5 flex items-center justify-center text-[#0C6170] mb-3">
                  <Bell size={20} className="text-[#0C6170]/50" />
                </div>
                <h4 className="text-xs font-bold text-gray-700">All caught up!</h4>
                <p className="text-[11px] text-gray-400 mt-1 max-w-[200px]">
                  No new notifications. We'll alert you when updates happen.
                </p>
              </div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item.id}
                  onClick={() => markAsRead(item.id)}
                  className={`group relative px-5 py-3.5 flex gap-3 cursor-pointer transition-colors duration-150 ${
                    item.read ? "bg-white hover:bg-gray-50/50" : "bg-teal-50/10 hover:bg-teal-50/20"
                  }`}
                >
                  {/* Status Indicator Dot */}
                  {!item.read && (
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#0C6170]" />
                  )}

                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full ${getBgColor(item.type)} flex items-center justify-center shrink-0 mt-0.5`}>
                    {getIcon(item.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pr-4">
                    <p className={`text-xs font-bold truncate leading-tight ${item.read ? "text-gray-800" : "text-gray-900"}`}>
                      {item.title}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1 leading-normal break-words">
                      {item.message}
                    </p>
                    <span className="text-[10px] text-gray-400 mt-1.5 inline-block">
                      {formatTime(item.time)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
